/**
 * 时间显示：一处解析，一处格式化。
 *
 * 后端存进 SQLite 的 DateTime 列是**不带时区的 UTC**（`func.now()` 就是
 * `CURRENT_TIMESTAMP`，也就是 UTC），序列化出来长这样：`2026-09-03T20:00:38`，
 * 没有 `Z` 也没有 `+08:00`。
 *
 * 而按 ECMAScript 规范，这种"只有日期时间、没有偏移量"的字符串会被
 * `new Date()` 当成**本地时间**。于是 `new Date('2026-09-03T20:00:38')` 在北京
 * 时区的浏览器里就是 9/3 20:00，再 `toLocaleString()` 打出来还是 9/3 20:00 ——
 * 一个真实发生在 9/4 04:00（北京）的推送，界面上显示成 9/3 20:00，**整整差 8 小时**。
 *
 * 所以这里的规则是：字符串里带明确偏移量就按它算，没带就按 UTC 算。
 */

/** 后端偶尔也会给带偏移量的时间（`datetime.now(timezone.utc)` 序列化后带 +00:00）。 */
const HAS_EXPLICIT_OFFSET = /(?:Z|[+-]\d{2}:?\d{2})$/i;

/** 纯日期（YYYY-MM-DD）本来就没有"几点"，当天处理，不要按 UTC 平移。 */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

export const DEFAULT_TIME_ZONE = 'Asia/Shanghai';

export type DateInput = string | number | Date | null | undefined;

/**
 * 把后端给的时间解析成 Date。没有偏移量的一律当 UTC。
 * 解析不出来返回 null，交给调用方决定显示什么。
 */
export function parseServerTime(value: DateInput): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  const text = value.trim();
  if (!text) return null;

  let normalized = text;
  if (!DATE_ONLY.test(text) && !HAS_EXPLICIT_OFFSET.test(text)) {
    // 空格分隔的形式（SQLite 直接读出来是 '2026-09-03 20:00:38'）也要能认。
    normalized = `${text.replace(' ', 'T')}Z`;
  }

  const parsed = new Date(normalized);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatter(timeZone: string, options: Intl.DateTimeFormatOptions) {
  try {
    return new Intl.DateTimeFormat('zh-CN', { ...options, timeZone });
  } catch {
    // 时区名写错了不该让整页白屏，退回默认时区。
    return new Intl.DateTimeFormat('zh-CN', { ...options, timeZone: DEFAULT_TIME_ZONE });
  }
}

const DATE_TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

const DATE_OPTIONS: Intl.DateTimeFormatOptions = {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

const TIME_OPTIONS: Intl.DateTimeFormatOptions = {
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

/** `2026-09-04 04:00:38`（按给定时区）。 */
export function formatDateTime(
  value: DateInput,
  timeZone: string = DEFAULT_TIME_ZONE,
  fallback = '-',
): string {
  const parsed = parseServerTime(value);
  if (!parsed) return fallback;
  return formatter(timeZone, DATE_TIME_OPTIONS).format(parsed).replace(/\//g, '-');
}

/** `2026-09-04`（按给定时区）。 */
export function formatDate(
  value: DateInput,
  timeZone: string = DEFAULT_TIME_ZONE,
  fallback = '-',
): string {
  const parsed = parseServerTime(value);
  if (!parsed) return fallback;
  return formatter(timeZone, DATE_OPTIONS).format(parsed).replace(/\//g, '-');
}

/** `04:00:38`（按给定时区），给图表坐标轴这类只需要时刻的地方。 */
export function formatTimeOfDay(
  value: DateInput,
  timeZone: string = DEFAULT_TIME_ZONE,
  fallback = '-',
): string {
  const parsed = parseServerTime(value);
  if (!parsed) return fallback;
  return formatter(timeZone, TIME_OPTIONS).format(parsed);
}

/** `09-04 04:00`（按给定时区），给卡片副标题这类空间紧的地方。 */
export function formatDateTimeShort(
  value: DateInput,
  timeZone: string = DEFAULT_TIME_ZONE,
  fallback = '-',
): string {
  const parsed = parseServerTime(value);
  if (!parsed) return fallback;
  return formatter(timeZone, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(parsed)
    .replace(/\//g, '-');
}

/** 当前时区相对 UTC 的偏移，例如 `UTC+08:00`（UTC 本身给 `UTC+00:00`）。
 *  时区名非法时返回空串，让调用方自己决定要不要显示。 */
export function timeZoneOffsetLabel(timeZone: string, at: Date = new Date()): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'longOffset',
    }).formatToParts(at);
    const name = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    return name.replace('GMT', 'UTC') || 'UTC';
  } catch {
    return '';
  }
}

/** 设置页给的候选时区。列表短一点，覆盖实际会用到的。 */
export const TIME_ZONE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Asia/Shanghai', label: '北京时间（中国标准时间）' },
  { value: 'Asia/Hong_Kong', label: '香港' },
  { value: 'Asia/Taipei', label: '台北' },
  { value: 'Asia/Tokyo', label: '东京' },
  { value: 'Asia/Singapore', label: '新加坡' },
  { value: 'Asia/Kolkata', label: '印度' },
  { value: 'Europe/London', label: '伦敦' },
  { value: 'Europe/Berlin', label: '柏林' },
  { value: 'Europe/Moscow', label: '莫斯科' },
  { value: 'America/New_York', label: '纽约（美东）' },
  { value: 'America/Chicago', label: '芝加哥（美中）' },
  { value: 'America/Los_Angeles', label: '洛杉矶（美西）' },
  { value: 'UTC', label: 'UTC（协调世界时）' },
];
